// src/services/permissions/productPermissionService.js
// Product-level permission service (subscription roles)

import { supabase } from '../../config';
import { ROLES, PRODUCTS } from '../constants/roleConstants';
import { checkSuperAdmin } from './adminPermissionService';

/**
 * Get user's global role for a product (subscription level)
 * @param {string} userId - User ID
 * @param {string} productName - Product name
 * @returns {Promise<string|null>} User's role or null
 */
export const getUserProductRole = async (userId, productName) => {
    try {
        // First check if super admin
        const isSuperAdmin = await checkSuperAdmin(userId);
        if (isSuperAdmin) return ROLES.SUPER_ADMIN;

        // Check product-specific subscription role
        const { data, error } = await supabase
            .from('user_product_roles')
            .select('role')
            .eq('user_id', userId)
            .eq('product_name', productName)
            .single();
        
        if (error) {
            if (error.code === 'PGRST116') { // No rows found
                return null;
            }
            throw error;
        }
        
        return data?.role || null;
    } catch (error) {
        console.error('Error getting user product role:', error);
        return null;
    }
};

/**
 * Check if user can create items in a product (subscription check)
 * @param {string} userId - User ID
 * @param {string} productName - Product name
 * @returns {Promise<boolean>} Whether user can create
 */
export const canCreateInProduct = async (userId, productName) => {
    try {
        const role = await getUserProductRole(userId, productName);
        // Only creators and super admins can create new items
        return role === ROLES.CREATOR || role === ROLES.SUPER_ADMIN;
    } catch (error) {
        console.error('Error checking create permission:', error);
        return false;
    }
};

/**
 * Get all products user has access to
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of products with roles
 */
export const getUserProducts = async (userId) => {
    try {
        const isSuperAdmin = await checkSuperAdmin(userId);
        
        if (isSuperAdmin) {
            // Super admin has access to all products
            return [
                { product_name: PRODUCTS.EQUITY_MANAGER, role: ROLES.SUPER_ADMIN },
                { product_name: PRODUCTS.DATAROOM, role: ROLES.SUPER_ADMIN },
                { product_name: PRODUCTS.CAPRAISE, role: ROLES.SUPER_ADMIN }
            ];
        }

        const { data, error } = await supabase
            .from('user_product_roles')
            .select('*')
            .eq('user_id', userId);
        
        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error getting user products:', error);
        return [];
    }
};
